import React from "react";

/**
 * Minimal markdown renderer for in-app PRD / spec viewing.
 *
 * Intentionally handles only the syntax our internal docs actually
 * use: # / ## / ### headings, lists (-), tables (| ... |), `inline`
 * code, **bold**, [links](href), --- horizontal rules, and blank-line
 * paragraph breaks. No HTML escape needed since we control the source.
 *
 * Why not react-markdown? Adding a 200KB dependency for ONE admin
 * read-only page is bad ROI. A 60-line custom renderer covers the
 * actual surface area we ship.
 */
export default function SimpleMarkdown({ source }) {
  if (!source) return null;
  const blocks = splitBlocks(source);

  return (
    <div className="space-y-4 text-[14px] leading-relaxed text-ink" data-testid="simple-markdown">
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

function splitBlocks(src) {
  // Split on blank lines, preserve table contiguity.
  const lines = src.split(/\r?\n/);
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push(buf.join("\n")); buf = []; } };
  for (const line of lines) {
    if (line.trim() === "") flush();
    else buf.push(line);
  }
  flush();
  return blocks;
}

function renderBlock(block, key) {
  const trim = block.trim();
  if (trim.startsWith("# ")) return <h1 key={key} className="font-spectral font-light text-4xl md:text-5xl tracking-tight leading-[1.05]">{inline(trim.slice(2))}</h1>;
  if (trim.startsWith("## ")) return <h2 key={key} className="font-spectral text-2xl tracking-tight mt-8 mb-1">{inline(trim.slice(3))}</h2>;
  if (trim.startsWith("### ")) return <h3 key={key} className="font-semibold text-base mt-6 mb-1">{inline(trim.slice(4))}</h3>;
  if (trim === "---") return <hr key={key} className="border-t border-[#EAEAEA] my-2" />;

  // Table
  if (/^\|.+\|/.test(trim) && trim.split("\n").length >= 2) {
    return renderTable(trim, key);
  }
  // Unordered list
  if (/^[-*]\s/.test(trim)) {
    const items = trim.split("\n").map(l => l.replace(/^[-*]\s+/, ""));
    return (
      <ul key={key} className="list-disc pl-5 space-y-1">
        {items.map((it, i) => <li key={i}>{inline(it)}</li>)}
      </ul>
    );
  }
  // Ordered list
  if (/^\d+\.\s/.test(trim)) {
    const items = trim.split("\n").map(l => l.replace(/^\d+\.\s+/, ""));
    return (
      <ol key={key} className="list-decimal pl-5 space-y-1">
        {items.map((it, i) => <li key={i}>{inline(it)}</li>)}
      </ol>
    );
  }
  // Code block
  if (trim.startsWith("```")) {
    const body = trim.replace(/^```\w*\n?/, "").replace(/\n?```$/, "");
    return (
      <pre key={key} className="bg-[#0E0E0E] text-gold-light/90 rounded-lg p-3 text-[12px] font-mono overflow-x-auto whitespace-pre">
        {body}
      </pre>
    );
  }
  // Paragraph
  return <p key={key} className="leading-relaxed">{inline(trim)}</p>;
}

function renderTable(block, key) {
  const rows = block.split("\n").filter(l => l.trim().length);
  const cells = (line) => line.split("|").map(c => c.trim()).filter((_, i, a) => i !== 0 && i !== a.length - 1 || a.length === 1);
  // Detect & drop the |---|---| separator row
  const isSep = (line) => /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(line.trim());
  const header = cells(rows[0]);
  const body = rows.slice(1).filter(r => !isSep(r)).map(cells);
  return (
    <div key={key} className="overflow-x-auto -mx-1">
      <table className="w-full text-[13px] border border-[#EAEAEA] rounded-lg">
        <thead className="bg-[#FAFAFA]">
          <tr>
            {header.map((h, i) => (
              <th key={i} className="text-left px-3 py-2 font-semibold text-[#6B6B6B] text-[11px] uppercase tracking-wider border-b border-[#EAEAEA]">{inline(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri} className="border-b border-[#F5F5F5] last:border-0">
              {r.map((c, ci) => <td key={ci} className="px-3 py-2 align-top">{inline(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function inline(text) {
  // Render **bold**, `code`, [text](href) in order. Returns JSX.
  const parts = [];
  let remaining = text;
  let i = 0;
  const patterns = [
    { re: /\*\*([^*]+)\*\*/, render: (m) => <strong key={`b-${i}`} className="font-semibold">{m[1]}</strong> },
    { re: /`([^`]+)`/, render: (m) => <code key={`c-${i}`} className="bg-[#FAFAFA] border border-[#EAEAEA] rounded px-1 py-[1px] text-[12.5px] font-mono">{m[1]}</code> },
    { re: /\[([^\]]+)\]\(([^)]+)\)/, render: (m) => <a key={`a-${i}`} href={m[2]} className="text-crimson hover:underline" target="_blank" rel="noreferrer">{m[1]}</a> },
  ];
  while (remaining.length) {
    let best = null;
    for (const p of patterns) {
      const m = remaining.match(p.re);
      if (m && (best === null || m.index < best.idx)) best = { idx: m.index, match: m, render: p.render };
    }
    if (!best) { parts.push(remaining); break; }
    if (best.idx > 0) parts.push(remaining.slice(0, best.idx));
    parts.push(best.render(best.match));
    remaining = remaining.slice(best.idx + best.match[0].length);
    i++;
  }
  return parts;
}
